import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, FieldError, Input, Label, Modal, TextArea, TextField } from '@heroui/react';

import Notice from '../../common/Notice';
import { SkeletonCard } from '../../common/Skeleton';
import { EmptyResult } from '../../common/LoadMore';
import * as topicsService from '../../../services/topics.service';
import { fieldErrorsOf } from '../../../services/apiClient';
import { messageOf, TOPICS } from '../../../i18n/el';

// **Τα θεματικά αντικείμενα** (D47, ΛΑ-05, ΛΑ-06).
//
// **Μία σελίδα για δύο κοινά**, και όχι δύο. Ο κάθε χρήστης βλέπει το λεξιλόγιο
// και μπορεί να πάει από ένα θέμα στις συλλογές του· ο διαχειριστής βλέπει **την
// ίδια** σελίδα με τα κουμπιά διαχείρισης. Χωριστή διαχειριστική οθόνη θα
// σήμαινε δύο τόπους για το ίδιο πράγμα, και ο διαχειριστής θα διόρθωνε το
// λεξιλόγιο χωρίς να βλέπει ποτέ πώς φαίνεται σε όλους τους άλλους.
//
// ⚠ **Ο έλεγχος είναι ΕΥΧΡΗΣΤΙΑ ΚΑΙ ΟΧΙ ΑΣΦΑΛΕΙΑ.** Ο φράχτης είναι το
// `topic:manage` του Πίνακα 1, στον εξυπηρετητή, με ζωντανή ανάγνωση της σημαίας
// (D39). Εδώ κρύβονται μόνο κουμπιά.

const EMPTY_FORM = { name: '', description: '' };

export default function TopicsPage({ isAdmin = false }) {
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [formError, setFormError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [isSaving, setSaving] = useState(false);

    const [removing, setRemoving] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        topicsService.getTopics().then(
            // ⚠ **Ο `apiClient` ΕΠΙΣΤΡΕΦΕΙ ΗΔΗ ΤΟ `data`, ΟΧΙ ΤΟ ΦΟΡΤΙΟ.** Η
            // αποδόμηση `({ data })` ζητούσε `data` **από τον πίνακα** και έδινε
            // `undefined` — λευκή σελίδα στην πρώτη επιτυχή απόκριση. Το μονοπάτι
            // ήταν **νεκρό** όσο η διαδρομή απαντούσε 500, οπότε το ελάττωμα δεν
            // μπορούσε να φανεί: ο πρώτος φράχτης έκρυβε τον δεύτερο.
            (data) => {
                setTopics(data ?? []);
                setLoading(false);
                setError('');
            },
            (caught) => {
                setError(messageOf(caught));
                setLoading(false);
            }
        );
    }, []);

    useEffect(() => { load(); }, [load]);

    const set = (name) => (value) => setForm((current) => ({ ...current, [name]: value }));

    function openCreate() {
        setEditing({ id: null });
        setForm(EMPTY_FORM);
        setFormError('');
        setFieldErrors({});
    }

    function openEdit(topic) {
        setEditing(topic);
        setForm({ name: topic.name, description: topic.description ?? '' });
        setFormError('');
        setFieldErrors({});
    }

    function handleSubmit(event) {
        event.preventDefault();
        setSaving(true);
        setFormError('');
        setFieldErrors({});

        const done = (message) => {
            setSaving(false);
            setEditing(null);
            setNotice(message);
            load();
        };
        const failed = (caught) => {
            setSaving(false);
            setFormError(messageOf(caught));
            setFieldErrors(fieldErrorsOf(caught));
        };

        if (editing?.id) {
            topicsService.updateTopic(editing.id, form).then(
                // Το πλήθος των συλλογών που ενημερώθηκαν έρχεται στο `meta`, και
                // **λέγεται**: η μετονομασία άγγιξε έγγραφα που ο διαχειριστής δεν
                // είδε, και χωρίς τον αριθμό δεν έχει τρόπο να το μάθει (ΜΛΑ-03).
                (payload) => done(TOPICS.renamed(payload?.meta?.renamedCollections ?? 0)),
                failed
            );
            return;
        }

        topicsService.createTopic(form).then(() => done(''), failed);
    }

    function handleRemove() {
        setSaving(true);
        topicsService.deleteTopic(removing.id).then(
            () => {
                setSaving(false);
                setRemoving(null);
                load();
            },
            (caught) => {
                setSaving(false);
                setRemoving(null);
                setError(messageOf(caught));
            }
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="text-xl font-semibold">{TOPICS.title}</h2>
                    <p className="text-sm text-muted">{TOPICS.subtitle}</p>
                </div>
                {isAdmin && <Button onPress={openCreate}>{TOPICS.create}</Button>}
            </div>

            {error && <Notice status="danger" title={TOPICS.title}>{error}</Notice>}
            {notice && <Notice status="success" title={TOPICS.title} onDismiss={() => setNotice('')}>{notice}</Notice>}

            {loading && <SkeletonCard />}

            {!loading && !error && topics.length === 0 && (
                <EmptyResult emptyMessage={isAdmin ? TOPICS.emptyAdmin : TOPICS.empty} />
            )}

            {!loading && topics.length > 0 && (
                <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
                    {topics.map((topic) => (
                        <Card key={topic.id}>
                            <Card.Header>
                                <Card.Title>{topic.name}</Card.Title>
                            </Card.Header>
                            <Card.Content className="flex flex-col gap-3">
                                {topic.description && (
                                    <p className="text-sm text-muted">{topic.description}</p>
                                )}

                                {/* Το πλήθος είναι **των ορατών** συλλογών, και ο
                                    σύνδεσμος οδηγεί σε κατάλογο με το ίδιο εύρος:
                                    ο αριθμός και η λίστα δεν επιτρέπεται να
                                    διαφωνούν. */}
                                <Link
                                    to={topicsService.collectionsPathForTopic(topic.name)}
                                    aria-label={TOPICS.openCollections(topic.name)}
                                    className="text-sm font-medium text-accent hover:underline"
                                >
                                    {TOPICS.collections(topic.collectionCount ?? 0)}
                                </Link>

                                {isAdmin && (
                                    <div className="flex flex-wrap gap-2">
                                        <Button size="sm" variant="outline" onPress={() => openEdit(topic)}>
                                            {TOPICS.edit}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onPress={() => setRemoving(topic)}
                                        >
                                            {TOPICS.remove}
                                        </Button>
                                    </div>
                                )}
                            </Card.Content>
                        </Card>
                    ))}
                </div>
            )}

            {/* D26: το `isOpen` σκέτο, και ο γονέας αποδίδει υπό συνθήκη. */}
            {editing && (
                <Modal.Root isOpen onOpenChange={() => setEditing(null)}>
                    <Modal.Backdrop>
                        <Modal.Container>
                            <Modal.Dialog>
                                <Modal.Header>
                                    <Modal.Heading>
                                        {editing.id ? TOPICS.edit : TOPICS.create}
                                    </Modal.Heading>
                                    <Modal.CloseTrigger />
                                </Modal.Header>
                                <Modal.Body>
                                    <form id="topic-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
                                        {formError && (
                                            <Notice status="danger" title={TOPICS.title}>{formError}</Notice>
                                        )}
                                        <TextField
                                            value={form.name}
                                            onChange={set('name')}
                                            isInvalid={Boolean(fieldErrors.name)}
                                            autoFocus
                                        >
                                            <Label>{TOPICS.name}</Label>
                                            <Input />
                                            <FieldError>{fieldErrors.name}</FieldError>
                                        </TextField>
                                        <TextField value={form.description} onChange={set('description')}>
                                            <Label>{TOPICS.description}</Label>
                                            <TextArea rows={3} />
                                        </TextField>
                                    </form>
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button variant="outline" type="button" onPress={() => setEditing(null)}>
                                        {TOPICS.cancel}
                                    </Button>
                                    <Button type="submit" form="topic-form" isPending={isSaving}>
                                        {TOPICS.save}
                                    </Button>
                                </Modal.Footer>
                            </Modal.Dialog>
                        </Modal.Container>
                    </Modal.Backdrop>
                </Modal.Root>
            )}

            {removing && (
                <Modal.Root isOpen onOpenChange={() => setRemoving(null)}>
                    <Modal.Backdrop>
                        <Modal.Container>
                            <Modal.Dialog>
                                <Modal.Header>
                                    <Modal.Heading>{TOPICS.removeTitle}</Modal.Heading>
                                    <Modal.CloseTrigger />
                                </Modal.Header>
                                <Modal.Body>
                                    {/* ⚠ **ΜΛΑ-03: η επιβεβαίωση ΟΝΟΜΑΖΕΙ ΤΟ ΠΛΗΘΟΣ**, και
                                        όταν το πλήθος δεν είναι μηδέν λέει ότι η ενέργεια
                                        **δεν θα γίνει** — αντί να την αφήσει να αποτύχει
                                        στον διακομιστή με 409 που ο χρήστης θα διάβαζε ως
                                        βλάβη.

                                        Το πλήθος εδώ είναι **των ορατών** συλλογών, ενώ ο
                                        φράχτης του διακομιστή μετρά **όλες**. Η διαφορά
                                        είναι δηλωμένη και όχι σφάλμα: ο διαχειριστής μπορεί
                                        να δει «καμία» και να πάρει 409 από ιδιωτική συλλογή
                                        που δεν είναι μέλος της. Το μήνυμα του διακομιστή
                                        λέει τότε το πραγματικό πλήθος. */}
                                    <p className="text-sm">
                                        {(removing.collectionCount ?? 0) > 0
                                            ? TOPICS.removeBlocked(removing.name, removing.collectionCount)
                                            : TOPICS.removeBody(removing.name)}
                                    </p>
                                </Modal.Body>
                                <Modal.Footer>
                                    <Button variant="outline" type="button" onPress={() => setRemoving(null)}>
                                        {TOPICS.cancel}
                                    </Button>
                                    <Button
                                        type="button"
                                        onPress={handleRemove}
                                        isPending={isSaving}
                                        isDisabled={(removing.collectionCount ?? 0) > 0}
                                    >
                                        {TOPICS.remove}
                                    </Button>
                                </Modal.Footer>
                            </Modal.Dialog>
                        </Modal.Container>
                    </Modal.Backdrop>
                </Modal.Root>
            )}
        </div>
    );
}
